import React, { useState, useRef, useEffect } from 'react';
import './HebrewPanel.css';
import { decodeParsingCode } from '../utils/hebrewParsing';
import strongsHebrew from '../data/strongs-hebrew.json';

/**
 * HebrewPanel
 *
 * Displays the OSHB morphologically-tagged Hebrew text for one OT chapter.
 * Each word is a clickable chip — clicking shows Strong's number, grammar,
 * and highlights the corresponding verse in the translated text.
 *
 * Props:
 *   chapterData      — array of verses; each verse is an array of word objects
 *                      { w: string, s: string, p: string }
 *   bookName         — English name of the current book (e.g. "Genesis")
 *   chapterNum       — 1-based chapter number
 *   onClose          — callback to close the panel
 *   onVerseHighlight — callback(verseNum|null, terms|null) — highlights the
 *                      corresponding verse in the translated text
 */
function HebrewPanel({ chapterData, bookName, chapterNum, onClose, onVerseHighlight }) {
  const [selectedWord, setSelectedWord] = useState(null);
  const detailRef = useRef(null);

  // Clear selection when chapter changes
  useEffect(() => {
    setSelectedWord(null);
    onVerseHighlight?.(null, null);
  }, [bookName, chapterNum]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll detail card into view when a word is selected
  useEffect(() => {
    if (selectedWord && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedWord]);

  const handleWordClick = (word, verseNum) => {
    const isDeselect = selectedWord === word;
    setSelectedWord(isDeselect ? null : word);
    if (isDeselect) {
      onVerseHighlight?.(null, null);
    } else {
      const glossRaw = strongsHebrew[word.s] || '';
      const terms = glossRaw.split(',').map(t => t.trim()).filter(Boolean);
      onVerseHighlight?.(verseNum, terms);
    }
  };

  const decoded = selectedWord ? decodeParsingCode(selectedWord.p) : null;
  const gloss   = selectedWord?.s ? (strongsHebrew[selectedWord.s] || '') : '';

  if (!chapterData) {
    return (
      <div className="Hebrew-panel">
        <div className="Hebrew-panel-header">
          <span className="Hebrew-panel-title">א Hebrew OT</span>
          <button className="Hebrew-panel-close" onClick={onClose} aria-label="Close Hebrew panel">✕</button>
        </div>
        <div className="Hebrew-panel-empty">
          No Hebrew text available for this book.
        </div>
      </div>
    );
  }

  return (
    <div className="Hebrew-panel">

      {/* ── Header ── */}
      <div className="Hebrew-panel-header">
        <span className="Hebrew-panel-title">
          <span className="Hebrew-aleph-badge">א</span>
          {bookName} {chapterNum}
        </span>
        <button className="Hebrew-panel-close" onClick={onClose} aria-label="Close Hebrew panel">✕</button>
      </div>

      {/* ── Word detail card ── */}
      {selectedWord && decoded && (
        <div className="Hebrew-word-detail" ref={detailRef}>
          <span className="Hebrew-detail-word" dir="rtl">{selectedWord.w}</span>
          <div className="Hebrew-detail-meta">
            {selectedWord.s && <span className="Hebrew-strongs">{selectedWord.s}</span>}
            <span className="Hebrew-pos">{decoded.pos}</span>
          </div>
          {decoded.prefixes?.length > 0 && (
            <div className="Hebrew-detail-prefixes">
              {decoded.prefixes.map((p, i) => (
                <span key={i} className="Hebrew-prefix-tag">+{p}</span>
              ))}
            </div>
          )}
          {decoded.details.length > 0 && (
            <div className="Hebrew-detail-tags">
              {decoded.details.map((d, i) => (
                <span key={i} className="Hebrew-tag">{d}</span>
              ))}
            </div>
          )}
          {gloss && (
            <span className="Hebrew-gloss">"{gloss.split(',')[0].trim()}"</span>
          )}
        </div>
      )}

      {/* ── Verse list ── */}
      <div className="Hebrew-verses">
        {chapterData.map((verseWords, vIdx) => (
          <div key={vIdx} className="Hebrew-verse">
            <span className="Hebrew-verse-num">{vIdx + 1}</span>
            <div className="Hebrew-words" dir="rtl">
              {verseWords.map((word, wIdx) => (
                <button
                  key={wIdx}
                  className={`Hebrew-word-chip${selectedWord === word ? ' active' : ''}`}
                  onClick={() => handleWordClick(word, vIdx + 1)}
                  title={`${word.s || ''} ${word.p || ''}`}
                >
                  {word.w}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

export default HebrewPanel;
